/**
 * Компонент HEAD указателя
 * Отображает текущую позицию HEAD в репозитории
 */

import { memo } from 'react';
import { cn } from '@/lib/utils';
import type { NodePosition, GraphConfig } from './types';
import { DEFAULT_GRAPH_CONFIG } from './types';

interface HeadPointerProps {
  position: NodePosition;
  isDetached?: boolean | undefined;
  currentBranch?: string | null | undefined;
  config?: Partial<GraphConfig> | undefined;
  isAnimating?: boolean | undefined;
}

/**
 * Компонент HEAD указателя
 */
export const HeadPointer = memo(function HeadPointer({
  position,
  isDetached = false,
  currentBranch,
  config,
  isAnimating = false,
}: HeadPointerProps) {
  const fullConfig: GraphConfig = { ...DEFAULT_GRAPH_CONFIG, ...config };

  // Позиция HEAD - слева от узла коммита
  const x = position.x - fullConfig.nodeRadius - 45;
  const y = position.y;

  // Текст для отображения
  const headText = isDetached ? 'HEAD' : 'HEAD';
  const subText = isDetached ? 'detached' : `→ ${currentBranch || 'main'}`;

  return (
    <g className="head-pointer" transform={`translate(${x}, ${y})`}>
      {/* Фон HEAD метки */}
      <rect
        x={-30}
        y={-12}
        width={60}
        height={24}
        rx={4}
        fill={isDetached ? '#ef4444' : '#F05032'}
        stroke="#ffffff"
        strokeWidth={1.5}
        className={cn('transition-all duration-200', isAnimating && 'animate-head-pulse')}
      />

      {/* Текст HEAD */}
      <text
        x={0}
        y={-2}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#ffffff"
        fontSize={10}
        fontFamily="monospace"
        fontWeight="bold"
        className="select-none pointer-events-none"
      >
        {headText}
      </text>

      {/* Подтекст (ветка или detached) */}
      <text
        x={0}
        y={8}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="rgba(255,255,255,0.7)"
        fontSize={8}
        fontFamily="monospace"
        className="select-none pointer-events-none"
      >
        {subText}
      </text>

      {/* Стрелка указывающая на коммит */}
      <polygon
        points={`
          32,-6
          32,6
          38,0
        `}
        fill={isDetached ? '#ef4444' : '#F05032'}
        className={cn('transition-all duration-200', isAnimating && 'animate-arrow-pulse')}
      />

      {/* Анимированное свечение при изменениях */}
      {isAnimating && (
        <circle
          cx={0}
          cy={0}
          r={20}
          fill="none"
          stroke="#F05032"
          strokeWidth={2}
          opacity={0.5}
          className="animate-head-glow"
        />
      )}
    </g>
  );
});

/**
 * Компонент для отображения HEAD с анимацией перемещения
 */
interface AnimatedHeadPointerProps extends HeadPointerProps {
  previousPosition?: NodePosition;
  animationDuration?: number;
}

export const AnimatedHeadPointer = memo(function AnimatedHeadPointer({
  position,
  previousPosition,
  animationDuration = 300,
  ...props
}: AnimatedHeadPointerProps) {
  // Если есть предыдущая позиция, показываем анимацию перемещения
  const isMoving =
    previousPosition && (previousPosition.x !== position.x || previousPosition.y !== position.y);

  return (
    <g
      className="animated-head-pointer"
      style={{
        transition: `transform ${animationDuration}ms ease-out`,
      }}
    >
      <HeadPointer
        position={position}
        isDetached={props.isDetached}
        currentBranch={props.currentBranch}
        config={props.config}
        isAnimating={isMoving || props.isAnimating}
      />
    </g>
  );
});
